import { Grid, useMediaQuery, useTheme, Button } from '@mui/material';
import React, { useState, useMemo, useEffect } from 'react';
import CustomSignaturePadDialog from 'component/Common/CustomSignaturePanel';
import { postApiCall } from 'api/apiService';
import { API_URL, UPLOAD_VENDOR_SIGN } from 'constant/app.config';
import Swal from 'sweetalert2';

const ViewSignature = () => {
  const theme = useTheme();
  const matchessm = useMediaQuery(theme.breakpoints.down('sm'));

  const [openSignPad, setOpenSignPad] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const userDetail = useMemo(
    () => JSON.parse(localStorage.getItem('userDetail') || '{}'),
    []
  );

  const userId = userDetail?.userId;
  const [step, setStep] = useState('review');

  useEffect(() => {
    // Check localStorage first
    const localSignature = Boolean(userDetail?.signUrl);

    // Check sessionStorage
    let sessionSignature = false;
    try {
      const sessionSign = sessionStorage.getItem('buyerSignature');
      if (sessionSign) {
        const parsed = JSON.parse(sessionSign);
        if (parsed.userId === userId) {
          sessionSignature = true;
        }
      }
    } catch (e) {
      console.error('Error parsing sessionStorage signature', e);
    }

    // Also check for signUrl in sessionStorage
    const sessionSignUrl = sessionStorage.getItem('signUrl');

    setHasSignature(
      localSignature || sessionSignature || Boolean(sessionSignUrl)
    );
  }, [userDetail?.signUrl, userId]);

  // ✅ Listen for signature updates from other components
  useEffect(() => {
    const handleSignatureUpdate = (event) => {
      if (event.detail?.userId === userId) {
        // Re-check signature status
        const sessionSign = sessionStorage.getItem('buyerSignature');
        if (sessionSign) {
          const parsed = JSON.parse(sessionSign);
          setHasSignature(true);
        } else {
          setHasSignature(Boolean(userDetail?.signUrl));
        }
      }
    };

    window.addEventListener('signatureUpdated', handleSignatureUpdate);

    return () => {
      window.removeEventListener('signatureUpdated', handleSignatureUpdate);
    };
  }, [userId, userDetail?.signUrl]);

  const handleSignatureSave = async (base64Image) => {
    try {
      try {
        sessionStorage.setItem(
          'buyerSignature',
          JSON.stringify({
            userId: userId,
            base64: base64Image,
            timestamp: new Date().toISOString(),
          })
        );
      } catch (e) {
        console.error('Failed to save to sessionStorage', e);
      }

      const blob = await (await fetch(base64Image)).blob();
      const file = new File([blob], 'signature.png', {
        type: 'image/png',
      });

      const formData = new FormData();
      formData.append('userId', userId);
      formData.append(
        'userType',
        userDetail?.primaryUserType === 'Solicitor' ? 'Solicitor' : 'User'
      );
      formData.append('requestFile', file);

      const res = await postApiCall(UPLOAD_VENDOR_SIGN, formData);
      if (res?.data?.status === 1) {
        const signUrl = res?.data?.signUrl;

        sessionStorage.setItem('signUrl', signUrl);

        try {
          const existingSignature = sessionStorage.getItem('buyerSignature');
          if (existingSignature) {
            const parsed = JSON.parse(existingSignature);
            if (parsed.userId === userId) {
              sessionStorage.setItem(
                'buyerSignature',
                JSON.stringify({
                  ...parsed,
                  signUrl: signUrl,
                  uploaded: true,
                })
              );
            }
          }
        } catch (e) {
          console.error('Failed to update sessionStorage signature', e);
        }

        const updatedUserDetail = {
          ...userDetail,
          signUrl: signUrl,
        };
        localStorage.setItem('userDetail', JSON.stringify(updatedUserDetail));
        window.dispatchEvent(new Event('storage'));

        Swal.fire({
          title: res?.data?.heading,
          text: res?.data?.message || 'Signature uploaded successfully',
          icon: 'success',
          confirmButtonText: 'Close',
        });

        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('signatureUpdated', {
              detail: { userId, signUrl },
            })
          );
        }
      } else {
        throw new Error(res?.data?.message || 'Upload failed');
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error?.message || 'Failed to upload signature',
        icon: 'error',
        confirmButtonText: 'Close',
      });
    } finally {
      setOpenSignPad(false);
    }
  };
  const signatureStatus = hasSignature
    ? 'Update Signature'
    : 'Create Signature';

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setOpenSignPad(true)}
        sx={{
          fontFamily: 'Poppins',
          backgroundColor: '#6C2AD2',
          color: 'white',
          textTransform: 'none',
          width: matchessm ? '100%' : 'auto',
          whiteSpace: 'nowrap',
          '&:hover': {
            backgroundColor: '#6C2AD2',
          },
        }}
      >
        {signatureStatus}
      </Button>

      <CustomSignaturePadDialog
        open={openSignPad}
        onClose={() => setOpenSignPad(false)}
        onSave={handleSignatureSave}
        signatureStatus={signatureStatus}
        isViewSignature={true}
      />
    </>
  );
};

export default ViewSignature;
